import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
  duplicateGroup,
  ProductGroupsFacade,
} from '@em/data-feed/data-access-products';
import {ProductGroupEntity} from '@em/data-feed/util-types';
import {CountryCode} from '@em/shared/util-types';
import {map, merge, Observable, of, switchMap, take, tap} from 'rxjs';

import {EditProductGroupComponent} from '../../product-groups/edit-product-group/edit-product-group.component';
import {NewProductGroupDialogComponent} from '../../product-groups/new-product-group-dialog/new-product-group-dialog.component';
import {outputToObservable} from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root', // Shared safely within UI/Feature scope
})
export class GroupDialogService {
  readonly #dialog = inject(MatDialog);
  readonly #facade = inject(ProductGroupsFacade);

  openAddEditGroup(
    productGroup?: ProductGroupEntity,
  ): Observable<ProductGroupEntity | null> {
    const dialogRef = this.#dialog.open(EditProductGroupComponent, {
      data: {group: productGroup},
      disableClose: true,
      height: '90vh',
      width: '1200px',
    });

    return merge(
      dialogRef.afterClosed().pipe(map(() => null)),
      outputToObservable(dialogRef.componentInstance.finishEditing).pipe(
        tap(() => dialogRef.close()),
      ),
    ).pipe(take(1));
  }

  openEditGroupAndSave(
    country: CountryCode,
    productGroup?: Partial<ProductGroupEntity>,
  ): Observable<string | null> {
    const dialogRef = this.#dialog.open(EditProductGroupComponent, {
      data: {group: productGroup},
      disableClose: true,
      height: '90vh',
      width: '1200px',
    });

    return merge(
      dialogRef.afterClosed().pipe(map(() => null)),
      outputToObservable(dialogRef.componentInstance.finishEditing).pipe(
        switchMap((productGroupEntity) => {
          if (productGroupEntity) {
            return this.#facade
              .saveGroup(productGroupEntity, country)
              .pipe(map((g) => g.uuid));
          } else {
            return of(null);
          }
        }),
        tap(() => {
          dialogRef.close();
        }),
      ),
    ).pipe(take(1));
  }

  openAddGroupAndSave(country: CountryCode) {
    const dialogRef = this.#dialog.open(NewProductGroupDialogComponent, {
      disableClose: true,
      width: '400px',
    });

    return merge(
      dialogRef.afterClosed().pipe(map(() => null)),
      outputToObservable(dialogRef.componentInstance.save),
    ).pipe(
      take(1),
      switchMap((selectTemplateResult) => {
        if (!selectTemplateResult) return of(null);

        const {templateName} = selectTemplateResult;
        if (templateName) {
          return this.#facade.addGroupByTemplateName(templateName, country);
        }
        return this.openEditGroupAndSave(country, undefined);
      }),
      tap(() => dialogRef.close()),
    );
  }

  duplicateAndEditGroup(
    productGroup: ProductGroupEntity,
  ): Observable<string | null> {
    const newGroup = duplicateGroup(productGroup);
    return this.openEditGroupAndSave(newGroup.country, newGroup);
  }
}
